import React, { useEffect, useMemo, useState } from "react";

import moment, { Moment } from "moment";
import { v4 as uuid } from "uuid";

import {
  isFeatureActiveOnCurrentFarm,
  isFieldEnrolled,
  useAddFieldPracticeParticipations,
  useBulkAddUpdateFieldCropPractice,
  useDeleteFieldPracticeParticipations,
  useGetAllCarbonProgramOutcomes,
  useGetFarmReportingYear,
  useGetFarms,
  useGetPracticeCrops,
  useTranslation,
  useValidateCropPeriods,
} from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropFormValidationError,
  FertilizerManagement,
  FieldListField,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeFCPO,
  PracticeSlug,
  VIEW_TYPE,
} from "models";

import {
  AlertBanner,
  Button,
  Drawer,
  FieldHeadlineBulk,
  Form,
  HideFCPOFeatureForUnenrolledFarms,
  HideFeature,
  Skeleton,
  StickyFooterButtons,
  Trans,
  Typography,
  Message as message,
} from "components";

import { PracticeBundleControls } from "pages/app/all-field-view/components/PracticeBundleControls.tsx";
import { UndersowingOverlapWarningModal } from "pages/app/all-field-view/components/UndersowingOverlapWarningModal.tsx";
import { BulkEditCropForm } from "pages/app/all-field-view/components/forms/crop/BulkEditCropForm";
import { AddEditOrganicAndMineralFertilizers } from "pages/app/all-field-view/components/forms/fertilizers/AddEditOrganicAndMineralFertilizers.tsx";
import { FertilizerManagementYesNoForm } from "pages/app/all-field-view/components/forms/fertilizers/FertilizerManagementYesNoForm.tsx";
import { AddEditTillageForm } from "pages/app/all-field-view/components/forms/tillage/AddEditTillageForm.tsx";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";
import {
  BulkAddEditCropFormProps,
  RequestWithExistingCrop,
  combineCropsAndPracticeRequest,
  createBulkCropPracticeRequestBody,
  createBulkPracticeRequest,
  createFertilizerManagementRequest,
  getDefaultBulkDataEntryDateValues,
  getFertilizerManagement,
  getPracticeConfigForBulk,
} from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { CROP_TYPE_TRANSLATION_KEY } from "pages/app/all-field-view/utils/CropTypeUtils";
import { getUndersowingWarningsForChanges } from "pages/app/all-field-view/utils/CropValidationUtils";
import { getCropRecommendedDates, getFirstEditableYear, getIsBaselineOrHistoricYear } from "pages/app/all-field-view/utils/dataEntryUtils.ts";
import {
  InputRequirementConfig,
  getDiverseCoverCropIncentive,
  getInputRequirementConfig,
  shouldShowInput,
} from "pages/app/all-field-view/utils/getInputRequirementConfig.ts";
import { getBaselineYearsByPracticeType } from "pages/app/field-timeline/utils/PracticeUtils.ts";

export type BulkEditCrop = CropFCPO & {
  fieldId: string;
  isMandatory: boolean;
  isBaseline: boolean;
  isPlaceholder?: boolean;
  availablePracticeBundles: PracticeBundle[];
  fertilizerManagement: FertilizerManagement[] | undefined;
  isInRedzone?: boolean;
};

export type BulkEditCropDrawerProps = {
  selectedCrops: BulkEditCrop[];
  selectedTillages: PracticeFCPO[];
  selectedFertilizers: PracticeFCPO[];
  allCrops: {
    [fieldId: string]: FieldPracticeResponse;
  } | null;
  affectedFields: FieldListField[];
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
  onFinish: () => void;
  // when set, the footer shows "Back" instead of "Cancel" — closes this drawer and returns to the crop type picker
  onBack?: () => void;
  // a real crop whose values seed the form's initial display (dates, yield, crop name, etc.) without being part
  // of selectedCrops itself — only selectedCrops get written on save, this crop is never touched
  templateCrop?: BulkEditCrop;
};

const getRequirementConfigForTillages = (inputRequirementConfigPerFieldAndCrop: Array<InputRequirementConfig | null>) =>
  inputRequirementConfigPerFieldAndCrop.reduce<InputRequirementConfig>((acc, config) => {
    if (!config) return acc;

    Object.keys(config).forEach((key) => {
      const currentLevel = acc[key as keyof InputRequirementConfig];
      const newLevel = config[key as keyof InputRequirementConfig];

      if (!newLevel) return;

      if (!currentLevel) {
        acc[key as keyof InputRequirementConfig] = newLevel;
      } else {
        // Priority: requiredForSaving > requiredForProgram > optional
        if (newLevel === "requiredForSaving" || (newLevel === "requiredForProgram" && currentLevel === "optional")) {
          acc[key as keyof InputRequirementConfig] = newLevel;
        }
      }
    });

    return acc;
  }, {});

const getRegionIdForMultipleFields = (affectedFields: FieldListField[]): string | undefined => {
  const regionIds = Array.from(
    new Set(
      affectedFields.map((field) => field.regionId).filter((id) => id !== "" && id != null) // removes empty string, null, undefined
    )
  );

  const allSame = regionIds.every((item, index, array) => item === array[0]);
  if (allSame) return regionIds[0];
  return undefined;
};

const currentYear = moment().year();

const getYear = (cropItem: BulkEditCrop[]): number => {
  console.assert(cropItem[0]?.year, "A crop item should always have a year!"); // eslint-disable-line
  return cropItem[0]?.year!; // eslint-disable-line
};

export const BulkEditCropDrawer = ({
  selectedCrops,
  allCrops,
  affectedFields,
  showDrawer,
  setShowDrawer,
  onFinish,
  onBack,
  selectedTillages,
  selectedFertilizers,
  templateCrop,
}: BulkEditCropDrawerProps) => {
  // Util Hooks
  const { t } = useTranslation(["allFieldView", "fieldTimeline"]);
  const [form] = Form.useForm<BulkAddEditCropFormProps>();
  const { currentFarm } = useGetFarms();
  const { validateCropChanges } = useValidateCropPeriods();
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  // API
  const {
    mutateAsync: updateBulk,
    isSuccess,
    isPending,
    isError,
  } = useBulkAddUpdateFieldCropPractice({ fieldIds: affectedFields.map((field) => field.id), farmId: currentFarm?.id });
  const { data: carbonProgramOutcomes, isLoading: isLoadingProgramOutcomes } = useGetAllCarbonProgramOutcomes(currentFarm?.id, currentFarm?.carbonProgram);
  const cropPracticesQuery = useGetPracticeCrops({ regionId: getRegionIdForMultipleFields(affectedFields) });
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { submissionStatusValue } = useSubmissionStatus(activeFarmReportingYear);
  const firstEditableYear = getFirstEditableYear(activeFarmReportingYear, submissionStatusValue?.lastSubmissionYear);

  // States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputRequirementConfigPerFieldAndCrop, setInputRequirementConfigPerFieldAndCrop] = useState<Array<InputRequirementConfig | null>>([]);
  const [inputRequirementConfigForMultipleFields, setInputRequirementConfigForMultipleFields] = useState<InputRequirementConfig | null>(null);
  const [disableTillageAndSeedingInputs, setDisableTillageAndSeedingInputs] = useState<boolean>(false);
  const [disableFertilizerInputs, setDisableFertilizerInputs] = useState<boolean>(false);
  const [cropPeriodValidationErrors, setCropPeriodValidationErrors] = useState<CropFormValidationError[]>([]);
  const [recommendedSeedingDate, setRecommendedSeedingDate] = useState<Moment>(moment());
  const [recommendedHarvestDate, setRecommendedHarvestDate] = useState<Moment>(moment());
  const [optionalInputsAreShown, setOptionalInputsAreShown] = useState<boolean>(true);
  const [showFieldDetails, setShowFieldDetails] = useState<boolean>(false);
  const [undersowingCropsBrokenByChange, setUndersowingCropsBrokenByChange] = useState<string[]>([]);
  const [pendingCropFormData, setPendingCropFormData] = useState<BulkAddEditCropFormProps | null>(null);

  // Practices
  const selectedMineralFertilizers = selectedFertilizers.filter((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const selectedOrganicFertilizers = selectedFertilizers.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

  const tillagesBulkShow = getPracticeConfigForBulk(selectedTillages, PRACTICE_SLUG.TILLAGES, submissionStatusValue?.lastSubmissionYear);
  const mineralBulkShow = getPracticeConfigForBulk(selectedMineralFertilizers, PRACTICE_SLUG.MINERAL_FERTILIZERS, submissionStatusValue?.lastSubmissionYear);
  // Mineral fertilizer is a per-field opt-in, so a selection only counts as opted in when every selected field is.
  // Without this the form gets no bundle at all and hides its inputs regardless of the fields' actual state.
  const mineralFieldIds = useMemo(() => Array.from(new Set(selectedCrops.map((crop) => crop.fieldId))), [selectedCrops]);
  const [mineralOptedInOverride, setMineralOptedInOverride] = useState<boolean | null>(null);
  const mineralPracticeBundle = useMemo<PracticeBundle | undefined>(() => {
    const bundles = selectedCrops.map((crop) => crop.availablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS));
    const firstBundle = bundles[0];
    if (!firstBundle) return undefined;
    return { ...firstBundle, active: mineralOptedInOverride ?? bundles.every((bundle) => !!bundle?.active) };
  }, [selectedCrops, mineralOptedInOverride]);
  const { mutate: addMineralParticipations } = useAddFieldPracticeParticipations(mineralFieldIds, currentFarm?.id);
  const { mutate: deleteMineralParticipations } = useDeleteFieldPracticeParticipations(mineralFieldIds, currentFarm?.id);

  // The opt-in is per field but the toggle speaks for the whole selection, so it fans out over every selected
  // field. Fire-and-forget, matching the single-crop drawers: the toggle reflects the user's choice immediately.
  const handleMineralBundleActivation = async (bundleSlug: PracticeSlug, active: boolean) => {
    setMineralOptedInOverride(active);
    if (active) {
      addMineralParticipations(mineralFieldIds.map((fieldId) => ({ fieldId, practiceSlugs: [bundleSlug] })));
    } else {
      mineralFieldIds.forEach((fieldId) => deleteMineralParticipations({ fieldId, practiceSlugs: [bundleSlug] }));
    }
  };

  const organicBulkShow = getPracticeConfigForBulk(selectedOrganicFertilizers, PRACTICE_SLUG.ORGANIC_FERTILIZERS, submissionStatusValue?.lastSubmissionYear);

  // Requirement configs
  const carbonProgram = currentFarm?.carbonProgram;
  const diverseCoverCropIncentive = useMemo(() => getDiverseCoverCropIncentive(carbonProgram), [carbonProgram]);
  // every selected field must be enrolled in the program before any of them can opt into mineral fertilizer
  const everyFieldIsEnrolled = selectedCrops.every((crop) => isFieldEnrolled(crop.availablePracticeBundles, carbonProgram ?? null));
  const farmIsEnrolledInCarbonProgram = !!carbonProgram?.id;
  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const { cropType } = selectedCrops[0];
  const year = getYear(selectedCrops);
  // only the add-crop (placeholder) flow gets the dynamic title — this drawer also edits existing crops
  const isAddFlow = selectedCrops.every((crop) => crop.isPlaceholder);
  const drawerTitle = isAddFlow
    ? t("allFieldView:titleBulkAddCropDrawer", {
        cropTypeName: t(`fieldTimeline:addCrops.cropTypeName.${CROP_TYPE_TRANSLATION_KEY[cropType]}`),
        count: affectedFields.length,
      })
    : t("allFieldView:titleBulkCropDrawer");
  // Check requirement config to see if there are even possible optional inputs
  const hideOptionalPracticeToggle = !Object.values(inputRequirementConfigForMultipleFields || {}).some((level) => level === "optional");
  const isCurrentYear = year === currentYear;

  // Red Zone
  const isInRedzone = affectedFields.some((field) => field.isInRedzone);

  const getRequirementConfigPerFieldAndSelectedCrop = () => {
    const { requirementConfigPerFieldAndCrop, errors, disableTillageAndSeedingInputs, disableFertilizerInputs } = selectedCrops.reduce(
      (acc, existingCrop) => {
        const field = affectedFields.find((field) => field.id === existingCrop.fieldId);
        if (!field) {
          acc.errors.push({
            fieldId: existingCrop.fieldId,
            cropId: existingCrop.cropId,
            message: "Could not find field for crop.",
          });
          return acc;
        }
        const carbonProgramResultOfField = carbonProgramOutcomes?.[field.id];

        // RequirementConfig
        const availablePracticeBundles = allCrops?.[existingCrop.fieldId]?.availablePracticeBundles ?? [];
        const fieldIsEnrolled = isFieldEnrolled(availablePracticeBundles, carbonProgram ?? null);
        const requirementConfig = getInputRequirementConfig(carbonProgram ?? null, availablePracticeBundles, existingCrop, fieldIsEnrolled);

        // Disable Tillage And Seeding Inputs based on availablePracticeBundles
        const baselineYearsByPracticeType = carbonProgramResultOfField ? getBaselineYearsByPracticeType(carbonProgramResultOfField) : {};
        const bundleHasConfirmedData = (bundleSlug: string) =>
          existingCrop.availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
        const cropYear = Number(existingCrop.year);

        const disableTillageAndSeedingInputs =
          (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.seedings, cropYear, firstEditableYear) ||
            getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.tillages, cropYear, firstEditableYear)) &&
          !!bundleHasConfirmedData("tillages");

        const disableFertilizerInputs =
          (getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.mineralFertilizers, cropYear, firstEditableYear) ||
            getIsBaselineOrHistoricYear(baselineYearsByPracticeType?.organicFertilizers, cropYear, firstEditableYear)) &&
          !!bundleHasConfirmedData("organicFertilizers");

        return {
          ...acc,
          requirementConfigPerFieldAndCrop: [...acc.requirementConfigPerFieldAndCrop, requirementConfig],
          disableTillageAndSeedingInputs: acc.disableTillageAndSeedingInputs || disableTillageAndSeedingInputs,
          disableFertilizerInputs: acc.disableFertilizerInputs || disableFertilizerInputs,
        };
      },
      {
        requirementConfigPerFieldAndCrop: [],
        errors: [],
        disableTillageAndSeedingInputs: false,
        disableFertilizerInputs: false,
      } as {
        requirementConfigPerFieldAndCrop: Array<InputRequirementConfig | null>;
        errors: { fieldId: string; cropId: string; message: string }[];
        disableTillageAndSeedingInputs: boolean;
        disableFertilizerInputs: boolean;
      }
    );

    if (errors.length > 0) {
      message({ content: errors[0].message, icon: "error" });
    }
    setInputRequirementConfigPerFieldAndCrop(requirementConfigPerFieldAndCrop);
    setDisableTillageAndSeedingInputs(disableTillageAndSeedingInputs);
    setDisableFertilizerInputs(disableFertilizerInputs);
  };

  const buildCropChanges = (bulkCropRequest: RequestWithExistingCrop[]): { fieldId: string; crop: CropFCPO }[] =>
    bulkCropRequest.map(({ bulkCropPracticesRequest, existingCrop }) => {
      const { cropId, hasResidue, seedingPracticeType, cropType, seedingStartDate, harvestEndDate } = bulkCropPracticesRequest.crop;
      const cropToValidate: CropFCPO = {
        ...existingCrop,
        seedingStartDate,
        harvestEndDate,
        cropId,
        hasResidue,
        seedingPracticeType: seedingPracticeType || null,
        cropType,
      };
      return { fieldId: bulkCropPracticesRequest.fieldId, crop: cropToValidate };
    });

  const getSavedCropsByField = (changes: { fieldId: string; crop: CropFCPO }[]): Record<string, CropFCPO[]> =>
    Object.fromEntries(changes.map(({ fieldId }) => [fieldId, allCrops?.[fieldId]?.crops ?? []]));

  const getCropValidationErrors = (bulkCropRequest: RequestWithExistingCrop[]): CropFormValidationError[] => {
    const changes = buildCropChanges(bulkCropRequest);
    return validateCropChanges(changes, getSavedCropsByField(changes));
  };

  const getUndersowingWarningsForBulkChange = (bulkCropRequest: RequestWithExistingCrop[]): string[] =>
    getUndersowingWarningsForChanges(buildCropChanges(bulkCropRequest), (fieldId) => allCrops?.[fieldId]?.crops ?? [], carbonProgram);

  const onSubmit = async (cropFormData: BulkAddEditCropFormProps, skipWarning?: boolean) => {
    setIsLoading(true);
    const touchedFormFields: Partial<BulkAddEditCropFormProps> = form.getFieldsValue({
      filter: (meta) => meta.touched,
    });

    const { data: fertilizerManagementRequestByCropItemId, errors: fertilizerManagementValidationErrors } = createFertilizerManagementRequest({
      formData: cropFormData,
      bulkEditCrops: selectedCrops,
      touchedFormFields: touchedFormFields,
      year: year,
      templateCrop,
    });

    const { data: tillageRequestByCropItemId, errors: bulkTillageValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedTillages,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: tillagesBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.TILLAGES,
    });

    const { data: mineralFertilizerRequestByCropItemId, errors: bulkMineralFertilizerValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedMineralFertilizers,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: mineralBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.MINERAL_FERTILIZERS,
    });

    const { data: organicRequestByCropItemId, errors: bulkOrganicFertilizerValidationErrors } = createBulkPracticeRequest({
      formData: cropFormData,
      selectedPractices: selectedOrganicFertilizers,
      bulkEditCrops: selectedCrops,
      touchedFormFields,
      bulkPracticeState: organicBulkShow?.bulkPracticeState,
      type: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
    });

    const { data, errors } = createBulkCropPracticeRequestBody({
      cropFormData: cropFormData.crop,
      touchedFormFields,
      bulkEditCrops: selectedCrops,
      speciesCountThreshold: diverseCoverCropIncentive?.speciesCountThreshold,
    });

    if (
      errors.length > 0 ||
      bulkTillageValidationErrors.length > 0 ||
      bulkMineralFertilizerValidationErrors.length > 0 ||
      bulkOrganicFertilizerValidationErrors.length > 0 ||
      fertilizerManagementValidationErrors.length > 0
    ) {
      message({ content: errors[0].message, icon: "error" });
      setIsLoading(false);
      return;
    }

    // Validate Dates
    const cropPeriodValidationErrors = getCropValidationErrors(data);

    if (cropPeriodValidationErrors.length) {
      setCropPeriodValidationErrors(cropPeriodValidationErrors);

      setTimeout(() => setCropPeriodValidationErrors([]), 4000);
      setIsLoading(false);
      const firstError = cropPeriodValidationErrors[0];
      message({ content: firstError.message, icon: "error" });
    } else {
      const undersowingWarnings = getUndersowingWarningsForBulkChange(data);
      if (!skipWarning && undersowingWarnings.length > 0) {
        setUndersowingCropsBrokenByChange(undersowingWarnings);
        setPendingCropFormData(cropFormData);
        setIsLoading(false);
        return;
      }

      const bulkCropPracticesRequest = data.map((data) => data.bulkCropPracticesRequest);
      // Combine Crop and Tillage request (Fertilizers are not included yet, they will not be sent and ignored in the API)
      const request = combineCropsAndPracticeRequest({
        crops: bulkCropPracticesRequest,
        practices: [...tillageRequestByCropItemId, ...organicRequestByCropItemId, ...mineralFertilizerRequestByCropItemId],
        fertilizerManagements: fertilizerManagementRequestByCropItemId,
      });
      // Need to use the mutateAsync since we need to show the user what crops could not be created/updated in the future
      await updateBulk({ data: request });
    }
    setIsLoading(false);
  };

  const incomplete = () => {
    message({ content: t("errors.required"), icon: "error" });
  };

  const onClose = () => {
    setShowDrawer(false);
  };

  const handleCropSelectionChanged = (cropId: string, year: number, cropType: CROP_TYPE_FCPO) => {
    // Find the selected crop from the practice crops data
    const practiceCrop = cropPracticesQuery.data?.find((crop) => crop.id === cropId);
    if (!practiceCrop) return;

    // I am using the selected crop's actual calendar dates as anchor points instead of the reporting `year` :D
    // This supports inventory programs where reporting may lag the grow year. Since only one cropType
    // and reporting year are allowed in the multi-select, the first crop in the selection is used as the anchor
    const anchorCrop = selectedCrops[0];
    const selectedYear =
      getCropYearFromDates(
        cropType,
        moment(anchorCrop?.seedingStartDate ?? anchorCrop?.fallbackDates?.seedingStartDate ?? undefined),
        moment(anchorCrop?.harvestEndDate ?? anchorCrop?.fallbackDates?.harvestEndDate ?? undefined)
      ) ?? year;

    // Update recommended dates based on the selected crop
    const recommendedDates = getCropRecommendedDates({ crop: practiceCrop, selectedYear, cropType });
    if (recommendedDates) {
      setRecommendedSeedingDate(recommendedDates.recommendedSeedingDate);
      setRecommendedHarvestDate(recommendedDates.recommendedHarvestDate);
    }
  };

  useEffect(() => {
    if (isSuccess && !isPending) {
      if (!isError) {
        onFinish();
      }
    }
  }, [isSuccess, isPending, onFinish, isError]);

  useEffect(() => {
    if (carbonProgramOutcomes) {
      getRequirementConfigPerFieldAndSelectedCrop();
    }
  }, [isLoadingProgramOutcomes, carbonProgramOutcomes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const requirementConfigForTillage = getRequirementConfigForTillages(inputRequirementConfigPerFieldAndCrop);
    setInputRequirementConfigForMultipleFields(requirementConfigForTillage);
  }, [inputRequirementConfigPerFieldAndCrop]);

  useEffect(() => {
    const { seedingDate, harvestDate } = getDefaultBulkDataEntryDateValues(selectedCrops);
    setRecommendedSeedingDate(seedingDate);
    setRecommendedHarvestDate(harvestDate);
  }, [selectedCrops]);

  const { hasMixedFertilizerManagement, showFertilizerManagement, fertilizerManagementValue } = useMemo(
    () => getFertilizerManagement(selectedCrops, year, cropType, templateCrop),
    [selectedCrops, year, cropType, templateCrop]
  );

  return (
    <Drawer
      dataTestId="bulk-edit-crop-drawer"
      title={drawerTitle}
      visible={showDrawer}
      onClose={onClose}
      destroyOnHidden={true}
      className="add-edit-crop-drawer"
    >
      <FieldHeadlineBulk
        affectedFields={affectedFields}
        selectedCrops={selectedCrops}
        selectedTillages={selectedTillages}
        selectedFertilizers={selectedFertilizers}
        showDetails={showFieldDetails}
        onToggleDetails={() => setShowFieldDetails((previous) => !previous)}
      />
      {/* TODO (Alp): Remove this after Soil+ 3.0 is done */}
      <HideFeature hide={isSoilPlus3DataGapFeatureActive}>
        <>
          {cropType !== CROP_TYPE_FCPO.UNDERSOWING && (
            <HideFCPOFeatureForUnenrolledFarms>
              <PracticeBundleControls
                bundles={[]}
                optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
                setOptionalInputsAreShown={setOptionalInputsAreShown}
                isCurrentYear={isCurrentYear}
                hideOptionalPractices={hideOptionalPracticeToggle}
                hidePracticeIcons={isInventoryProgram}
                viewType={VIEW_TYPE.ALL_FIELDS}
              />
            </HideFCPOFeatureForUnenrolledFarms>
          )}
        </>
      </HideFeature>

      {/* TODO (Alp): Use "Inline banner" when https://klim-app.atlassian.net/browse/FOOT-1355 is done */}
      <AlertBanner
        className="margin-top-200"
        description={<Trans i18nKey={"allFieldView:cropForm.multiEditInfo"} components={{ strong: <strong /> }} />}
        type="info-highlighted"
        iconName="tabler/info-circle"
        closeable={true}
        iconAlign="start"
        gap={0}
      />
      {isLoadingProgramOutcomes ? (
        <Skeleton type="block" height="15rem" className="margin-top-200" />
      ) : (
        <Form form={form} name="bulkCropForm" onFinish={onSubmit} onFinishFailed={incomplete}>
          <BulkEditCropForm
            selectedItems={templateCrop ? [{ ...templateCrop, isConfirmed: false }, ...selectedCrops] : selectedCrops}
            inputRequirementConfigPerFieldAndCrop={inputRequirementConfigPerFieldAndCrop}
            optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
            cropPeriodValidationErrors={cropPeriodValidationErrors}
            disableTillageAndSeedingInputs={disableTillageAndSeedingInputs}
            regionId={getRegionIdForMultipleFields(affectedFields)}
            year={year}
            recommendedSeedingDate={recommendedSeedingDate}
            recommendedHarvestDate={recommendedHarvestDate}
            onCropSelectionChanged={handleCropSelectionChanged}
            isInRedzone={isInRedzone}
          />

          {(shouldShowInput(inputRequirementConfigForMultipleFields?.tillages, optionalInputsAreShown || !farmIsEnrolledInCarbonProgram) ||
            selectedTillages.length > 0) && (
            <AddEditTillageForm
              baselineYears={[]} // Not needed since we can have a mixed values
              bulkEditMode={selectedCrops.length > 1}
              cropId={uuid()} // We only need the cropId to create a TILLAGE_BLANK_PRACTICE, the cropId of the TILLAGE_BLANK_PRACTICE is not getting used for anything else
              disabled={disableTillageAndSeedingInputs}
              existingTillages={tillagesBulkShow?.practiceToShow ?? []}
              optionalInputsAreShown={optionalInputsAreShown || !farmIsEnrolledInCarbonProgram}
              practiceConfigForBulk={tillagesBulkShow ?? undefined}
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              requirePreciseTillageDepth={carbonProgram?.fieldsManagement.requirePreciseTillageDepth === true}
              requireTillageSoilMixing={carbonProgram?.fieldsManagement.requireTillageSoilMixing === true}
              requirementConfig={inputRequirementConfigForMultipleFields}
              year={year}
            />
          )}

          {/* Fertilizer Forms */}
          {(shouldShowInput(inputRequirementConfigForMultipleFields?.fertilizerManagement, optionalInputsAreShown || !farmIsEnrolledInCarbonProgram) ||
            showFertilizerManagement) && (
            <FertilizerManagementYesNoForm
              initialValue={fertilizerManagementValue ?? null}
              requirementLevel={inputRequirementConfigForMultipleFields?.fertilizerManagement}
              mixedValues={hasMixedFertilizerManagement}
            />
          )}

          {(shouldShowInput(inputRequirementConfigForMultipleFields?.organicFertilizers, optionalInputsAreShown || !farmIsEnrolledInCarbonProgram) ||
            selectedFertilizers.some(({ type }) => type === PRACTICE_SLUG.ORGANIC_FERTILIZERS)) && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="organic"
              disabled={disableFertilizerInputs}
              existingFertilizers={organicBulkShow?.practiceToShow ?? []}
              baselineYears={[]} // Not needed since we can have a mixed values
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              year={year}
              currency={carbonProgram?.currency}
              requirementLevel={inputRequirementConfigForMultipleFields?.mineralFertilizers}
              practiceConfigForBulk={organicBulkShow ?? undefined}
              bulkEditMode={selectedCrops.length > 1}
              carbonProgramMethodology={carbonProgram?.accountingMethodology}
            />
          )}

          {(shouldShowInput(inputRequirementConfigForMultipleFields?.mineralFertilizers, optionalInputsAreShown || !farmIsEnrolledInCarbonProgram) ||
            selectedFertilizers.some(({ type }) => type === PRACTICE_SLUG.MINERAL_FERTILIZERS)) && (
            <AddEditOrganicAndMineralFertilizers
              chosenFertilizerType="mineral"
              disabled={disableFertilizerInputs}
              existingFertilizers={mineralBulkShow?.practiceToShow ?? []}
              practiceBundle={mineralPracticeBundle}
              onBundleActivation={handleMineralBundleActivation}
              fieldIsEnrolled={everyFieldIsEnrolled}
              baselineYears={[]} // Not needed since we can have a mixed values
              recommendedSeedingDate={recommendedSeedingDate.toISOString()}
              year={year}
              currency={carbonProgram?.currency}
              requirementLevel={inputRequirementConfigForMultipleFields?.mineralFertilizers}
              practiceConfigForBulk={mineralBulkShow ?? undefined}
              bulkEditMode={selectedCrops.length > 1}
              carbonProgramMethodology={carbonProgram?.accountingMethodology}
            />
          )}
          <StickyFooterButtons
            absolute
            extras={
              isAddFlow && (
                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                  {t("allFieldView:bulkCropDrawerFooterHint")}
                </Typography>
              )
            }
          >
            <div className="row-space-between">
              <Button dataTestId="bulk-edit-cancel" onClick={onBack ?? onClose}>
                {onBack ? t("allFieldView:backBulkCropDrawer") : t("allFieldView:cancelBulkCropDrawer")}
              </Button>
              <Button dataTestId="bulk-edit-save" loading={isLoading} type="primary" htmlType="submit">
                {t("allFieldView:saveCropDrawer")}
              </Button>
            </div>
          </StickyFooterButtons>
        </Form>
      )}
      <UndersowingOverlapWarningModal
        dataTestId="bulk-crop-form-undersowing-overlap-warning"
        cropNames={undersowingCropsBrokenByChange}
        onCancel={() => {
          setUndersowingCropsBrokenByChange([]);
          setPendingCropFormData(null);
        }}
        onConfirm={() => {
          setUndersowingCropsBrokenByChange([]);
          if (pendingCropFormData) onSubmit(pendingCropFormData, true);
        }}
      />
    </Drawer>
  );
};
